import * as React from 'react';
import Header from '/react4xp/components/prototype/Header';
import type {HeaderNavItem} from '/react4xp/components/prototype/Header/types';
import type {HeaderMenuColumn, HeaderMenuLink} from '/react4xp/components/prototype/HeaderMenu/types';
import type {LanguageOption} from '/react4xp/components/prototype/LanguageSelector/types';
import type {MainNavigationData, ResolvedColumn, ResolvedLink, ResolvedNavItem} from '/types/CommonData';

function toHeaderMenuLink(link: ResolvedLink): HeaderMenuLink {
    return {label: link.label, url: link.url, isExternal: link.isExternal};
}

function toHeaderMenuColumn(column: ResolvedColumn): HeaderMenuColumn | null {
    if (column.type === 'promo') {
        return {
            type: 'promo',
            heading: column.title ?? '',
            description: column.text ?? '',
            ctaLabel: column.cta?.label ?? '',
            ctaUrl: column.cta?.url,
            ctaIsExternal: column.cta?.isExternal ?? false,
        };
    }
    // HeaderMenu always renders mainLink as a real link, so a column without a
    // configured heading falls back to its first sub-page rather than a blank,
    // href="#" link. A column with neither is dropped (see toHeaderNavItem).
    const [mainLink, ...links] = column.heading
        ? [toHeaderMenuLink(column.heading), ...column.subPages.map(toHeaderMenuLink)]
        : column.subPages.map(toHeaderMenuLink);
    return mainLink ? {type: 'links', mainLink, links} : null;
}

function toHeaderNavItem(item: ResolvedNavItem): HeaderNavItem {
    const columns = item.menuColumns
        .map(toHeaderMenuColumn)
        .filter((column): column is HeaderMenuColumn => column !== null);
    return {
        label: item.label ?? '',
        url: item.link?.url,
        isExternal: item.link?.isExternal,
        columns: columns.length > 0 ? columns : undefined,
    };
}

export function MainNavigation({data}: {data?: MainNavigationData}) {
    if (!data) {
        return null;
    }

    // Captured in a const so the `data` narrowing above still holds inside the
    // onLanguageSelect closure below (TS doesn't carry parameter narrowing into
    // nested functions).
    const navData = data;

    const languageOptions: LanguageOption[] = navData.languages.map((language) => ({
        value: language.id,
        label: language.language || language.id,
    }));
    const activeLanguage = navData.languages.find((language) => language.isCurrent)?.id;

    function handleLanguageSelect(value: string) {
        const target = navData.languages.find((language) => language.id === value);
        if (target) {
            window.location.href = target.url;
        }
    }

    return (
        <Header
            logo={{src: navData.logoUrl, hoverSrc: navData.logoHoverUrl, url: navData.logoLinkUrl, alt: '99x'}}
            nav={navData.navItems.map(toHeaderNavItem)}
            cta={navData.contactCta}
            languageOptions={languageOptions}
            activeLanguage={activeLanguage}
            onLanguageSelect={handleLanguageSelect}
        />
    );
}
